import { createBrowserRouter, type LoaderFunctionArgs, redirect } from "react-router";
import { RouteError } from "./components/RouteError.tsx";
import { fieldBenchRoute, fieldRoutes } from "./field/routes.tsx";
import { AppLoading, RootLayout } from "./root.tsx";
import { NotFound } from "./routes/not-found.tsx";

/**
 * React Router 8, data mode (SPA). Route modules export `loader`, `action`
 * and `Component` and load lazily, so a phone downloads the office screens
 * it opens and not the rest; the service worker precaches every chunk for
 * offline use.
 *
 * Signed-out pages and the public customer view sit beside the app shell;
 * everything inside the shell ("app") needs a session and an active tenant.
 */

const toQuoteTab = ({ params }: LoaderFunctionArgs) =>
  redirect(`/quotes/${encodeURIComponent(params.id ?? "")}/design`);

export const router = createBrowserRouter([
  {
    id: "root",
    path: "/",
    Component: RootLayout,
    HydrateFallback: AppLoading,
    ErrorBoundary: RouteError,
    children: [
      { path: "sign-in", lazy: () => import("./routes/auth/sign-in.tsx") },
      { path: "sign-in/code", lazy: () => import("./routes/auth/code.tsx") },
      { path: "sign-out", lazy: () => import("./routes/auth/sign-out.tsx") },
      { path: "organisation", lazy: () => import("./routes/auth/organisation.tsx") },
      { path: "organisation/new", lazy: () => import("./routes/auth/organisation-new.tsx") },
      { path: "view/:token", lazy: () => import("./routes/public/view.tsx") },
      // The laser bench test works signed out (spike S3), so it sits outside the shell.
      fieldBenchRoute,
      {
        id: "app",
        lazy: () => import("./layouts/AppShell.tsx"),
        children: [
          {
            // Errors inside a page render here, so the nav stays usable.
            ErrorBoundary: RouteError,
            children: [
              { index: true, lazy: () => import("./routes/dashboard.tsx") },
              { path: "field", children: fieldRoutes },
              { path: "quotes", lazy: () => import("./routes/quotes/list.tsx") },
              { path: "quotes/new", lazy: () => import("./routes/quotes/new.tsx") },
              {
                id: "quote",
                path: "quotes/:id",
                lazy: () => import("./routes/quotes/workspace/layout.tsx"),
                children: [
                  { index: true, loader: toQuoteTab },
                  { path: "design", lazy: () => import("./routes/quotes/workspace/design.tsx") },
                  {
                    path: "layout",
                    lazy: () => import("./routes/quotes/workspace/layout-tab.tsx"),
                  },
                  { path: "3d", lazy: () => import("./routes/quotes/workspace/three-d.tsx") },
                  { path: "price", lazy: () => import("./routes/quotes/workspace/price.tsx") },
                  {
                    path: "revisions",
                    lazy: () => import("./routes/quotes/workspace/revisions.tsx"),
                  },
                ],
              },
              { path: "enquiries", lazy: () => import("./routes/enquiries/list.tsx") },
              { path: "enquiries/:id", lazy: () => import("./routes/enquiries/detail.tsx") },
              { path: "customers", lazy: () => import("./routes/customers/list.tsx") },
              { path: "customers/new", lazy: () => import("./routes/customers/new.tsx") },
              { path: "customers/:id", lazy: () => import("./routes/customers/detail.tsx") },
              { path: "customers/:id/edit", lazy: () => import("./routes/customers/edit.tsx") },
              { path: "sites/:id/edit", lazy: () => import("./routes/customers/site-edit.tsx") },
              {
                path: "settings",
                lazy: () => import("./routes/settings/layout.tsx"),
                children: [
                  { index: true, loader: () => redirect("/settings/branding") },
                  { path: "branding", lazy: () => import("./routes/settings/branding.tsx") },
                  { path: "rates", lazy: () => import("./routes/settings/rates.tsx") },
                  { path: "members", lazy: () => import("./routes/settings/members.tsx") },
                ],
              },
              { path: "more", lazy: () => import("./routes/more.tsx") },
              { path: "*", Component: NotFound },
            ],
          },
        ],
      },
    ],
  },
]);
